// Snapshot, then stream — how a console keeps a live view without rebuilding it from events
// (spec/09-realtime-protocol.md §4; INFRA-18, #379).
//
//   1. Subscribe to the channel. Buffer events.
//   2. Fetch the snapshot over REST (it returns a `version`).
//   3. Apply buffered and later events whose `version` is greater than the snapshot's.
//   4. If an event's `version` skips (a gap), fetch the snapshot again.
//
// "There is no server-side history replay for consoles; the snapshot is the recovery mechanism." So
// this never asks the gateway for missed events: a gap — a frame dropped while a socket was slow
// (spec/09 §8), a reconnect, a node restart — is answered with a fresh snapshot, and the view
// converges instead of drifting.
//
// Three cases an event can be, once live:
//
//   version = current + 1       the next one: applied
//   version ≤ current           already in the snapshot or already applied: ignored — unless it is
//                               far below (a counter that restarted, after a Valkey wipe or the weekly
//                               expiry), which is treated as a gap
//   version > current + 1       a gap: buffered, and the snapshot is fetched again
//
// An event without a version (`settings.changed` on a venue's floor channel) is not part of the
// sequence; it is handed to `onUnversioned` and never causes a gap.
//
// Pure of transport: it is told events and asks for snapshots through the functions it is given, so
// the console, the staff app and a test drive it the same way.
/** How far below the current version an event must be to mean "the counter restarted". */
export const VERSION_RESTART_WINDOW = 1000;
export function createSnapshotStream(options) {
    let state = 'buffering';
    let view;
    let version;
    let buffer = [];
    let fetching;
    let fetches = 0;
    function publish() {
        if (view !== undefined && version !== undefined)
            options.onView?.(view, version);
    }
    /** Apply what is buffered above the current version, in order; `false` at the first gap. */
    function drain() {
        buffer.sort((a, b) => a.version - b.version);
        const rest = [];
        let gap = false;
        for (const item of buffer) {
            if (gap) {
                rest.push(item);
                continue;
            }
            if (item.version <= version)
                continue;
            if (item.version === version + 1) {
                view = options.apply(view, item.event);
                version = item.version;
                continue;
            }
            gap = true;
            rest.push(item);
        }
        buffer = rest;
        return !gap;
    }
    function resync(reason) {
        if (state === 'stopped')
            return;
        options.onResync?.(reason);
        state = 'resyncing';
        void load();
    }
    function load() {
        fetching ??= (async () => {
            try {
                const fetched = await options.fetchSnapshot();
                fetches += 1;
                if (state === 'stopped')
                    return;
                view = fetched.snapshot;
                version = fetched.version;
                const contiguous = drain();
                publish();
                if (contiguous) {
                    state = 'live';
                }
                else {
                    // What the socket delivered while fetching already skips past the snapshot: fetch again.
                    fetching = undefined;
                    options.onResync?.('gap');
                    await load();
                }
            }
            finally {
                fetching = undefined;
            }
        })();
        return fetching;
    }
    return {
        get state() {
            return state;
        },
        get version() {
            return version;
        },
        get view() {
            return view;
        },
        get fetches() {
            return fetches;
        },
        start: () => load(),
        receive(event) {
            if (state === 'stopped')
                return;
            const eventVersion = options.versionOf(event);
            if (eventVersion === undefined) {
                options.onUnversioned?.(event);
                return;
            }
            if (state !== 'live' || version === undefined) {
                buffer.push({ event, version: eventVersion });
                return;
            }
            if (eventVersion === version + 1) {
                view = options.apply(view, event);
                version = eventVersion;
                publish();
                return;
            }
            if (eventVersion <= version) {
                // Already applied, or already in the snapshot — unless the counter has restarted.
                if (eventVersion <= version - VERSION_RESTART_WINDOW) {
                    buffer = [{ event, version: eventVersion }];
                    resync('restart');
                }
                return;
            }
            buffer.push({ event, version: eventVersion });
            resync('gap');
        },
        async settled() {
            while (fetching !== undefined)
                await fetching;
        },
        stop() {
            state = 'stopped';
            buffer = [];
        },
    };
}
//# sourceMappingURL=snapshot-stream.js.map